import { useEffect, useMemo, useState } from "react"
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { useParams } from "react-router"
import { navigate } from "lib/routes/navigate"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useCurrentUser } from "lib/store/use-current-user"
import { useDialog } from "lib/@hooks/useDialog"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { If } from "lib/@components/switch"
import { scenario } from "routes/scenarios/controller/scenarios"
import { recalculateScenario } from "routes/scenarios/controller/recalculate-scenario"
import { promoteScenarioToDraftRegime } from "routes/scenarios/controller/promote-scenario"
import { addVariant as addVariantApi } from "routes/scenarios/controller/add-variant"
import { updateVariant as updateVariantApi } from "routes/scenarios/controller/update-variant"
import { removeVariant as removeVariantApi } from "routes/scenarios/controller/remove-variant"
import { recalculateVariant as recalculateVariantApi } from "routes/scenarios/controller/recalculate-variant"
import { reorderVariants as reorderVariantsApi } from "routes/scenarios/controller/reorder-variants"
import { ruleSetsByIds } from "routes/rule-sets/controller/rule-sets-by-ids"
import { updateRuleSet } from "routes/rule-sets/controller/update-rule-set"
import { stripRuleSetRules, stripRuleSetRulesForCache } from "../rule-sets/strip-rule-set-rules"
import { exportScenarioToExcel } from "./export-scenario-to-excel"
import { runScenarioCostExport } from "./run-scenario-cost-export"
import { ScenarioCostSummary } from "./scenario-cost-summary"
import { VariantPanel } from "./variant-panel"
import { AddVariantDialog } from "./add-variant-dialog"
import { EditRuleSetInScenarioDialog } from "./edit-rule-set-in-scenario-dialog"
import { PromoteScenarioDialog } from "./promote-scenario-dialog"
import { RulesPanel } from "./rules-panel"
import { useCreateRuleSetInScenario } from "./use-create-rule-set-in-scenario"
import { usePromotionNotifications } from "./use-promotion-notifications"
import { PromotedRegimeBanner } from "./promoted-regime-banner"
import { useDemand } from "lib/authorization/use-demand"
import {
    areRuleSetIdsEqual,
    buildRecalculationPrompt,
    byId,
    getRuleSetIdsForVariant,
    hasCalculationRelevantRuleSetChange,
    hasRules,
    haveSameIds,
    isSettledVariantOverride,
    mergeRuleSetIds,
    notMatchingId,
    restoreRuleSetReferenceNames,
    toId,
    toRuleSetEntry,
    toRuleSetForDialog,
} from "./scenario-view-utils"

const parentCrumbs = [{ label: "Optimiser", to: "/app/optimiser" }]

export function ScenarioView() {
    const { id } = useParams()
    const data = scenario.useResults({ id }, withInitialValue(IS_LOADING))
    const canManageOptimiser = useDemand("manageScenarios")
    const currentUser = useCurrentUser()
    const [menuAnchor, setMenuAnchor] = useState(null)
    const [selectedVariantId, setSelectedVariantId] = useState(null)
    const [variantPanelOpen, setVariantPanelOpen] = useState(false)
    const [rulesPanelOpen, setRulesPanelOpen] = useState(false)
    const [cachedRuleSetsById, setCachedRuleSetsById] = useState({})
    const [variantOverridesById, setVariantOverridesById] = useState({})
    const { promotedRegimeId, clearPromotedRegime, notifyPromotionStarted } = usePromotionNotifications(id)
    const createRuleSetForScenario = useCreateRuleSetInScenario()

    const openAddDialog = useDialog(<AddVariantDialog scenarioId={id} />, { maxWidth: "sm", fullWidth: true })
    const openEditDialog = useDialog(<AddVariantDialog scenarioId={id} />, { maxWidth: "sm", fullWidth: true })
    const openPromoteDialog = useDialog(<PromoteScenarioDialog />, { maxWidth: "sm", fullWidth: true })
    const openEditRuleSetDialog = useDialog(<EditRuleSetInScenarioDialog />, { maxWidth: "md", fullWidth: true })

    const variants = getVariants()
    const effectiveVariantId = selectedVariantId || variants[0]?.id
    const selectedVariant = findSelectedVariant()

    const variantRuleSetIds = useMemo(getVariantRuleSetIds, [selectedVariant])
    const variantRuleSetsResult = ruleSetsByIds.useResults(variantRuleSetIds.length ? { ids: variantRuleSetIds } : null)
    const effectiveRuleSets = useMemo(buildEffectiveRuleSets, [
        variantRuleSetIds,
        variantRuleSetsResult,
        cachedRuleSetsById,
        selectedVariant,
    ])
    const appliedRuleSetIds = getAppliedRuleSetIds()
    const ruleSetCount = appliedRuleSetIds.length

    const isCalculating = selectedVariant?.status === "calculating"
    // A failed recalculation preserves the last successful result, so exporting stays
    // valid: its detailsRef still points at a ready generation. Only a result that is
    // itself an error shape (no previous result to keep) blocks export.
    const canExport = !!selectedVariant?.costResult && !selectedVariant.costResult.error
    const variantActions = getVariantActions()
    const rulesActions = getRulesActions()
    const costSummaryActions = getCostSummaryActions()

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(clearSettledVariantOverrides, [data?.variants, variantOverridesById])

    if (data === IS_LOADING) return <LoadingScreen description="Loading scenario..." />

    if (!data) {
        return (
            <PageBreadcrumbContainer title="Scenario Not Found" parentCrumbs={parentCrumbs} sx={{ height: 1 }}>
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                        This scenario could not be found.
                    </Typography>
                </Stack>
            </PageBreadcrumbContainer>
        )
    }

    return (
        <PageBreadcrumbContainer
            title={data?.name ?? "Scenario"}
            heading={
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", display: "inline-flex" }}>
                    <Box>{data?.name ?? "Scenario"}</Box>
                    <IconButton
                        data-cy="scenario-detail-menu"
                        aria-label="Scenario actions"
                        size="small"
                        onClick={handleOpenMenu}
                    >
                        <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
                        <If value={canManageOptimiser}>
                            <MenuItem onClick={handleEdit}>
                                <ListItemIcon>
                                    <Iconify icon="solar:pen-bold" />
                                </ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                        </If>
                        <MenuItem onClick={handleExport} disabled={!canExport}>
                            <ListItemIcon>
                                <Iconify icon="mdi:microsoft-excel" />
                            </ListItemIcon>
                            <ListItemText>Export to Excel</ListItemText>
                        </MenuItem>
                        <If value={canManageOptimiser}>
                            <MenuItem onClick={handleRecalculateAll} disabled={isCalculating}>
                                <ListItemIcon>
                                    <Iconify icon="mdi:refresh" />
                                </ListItemIcon>
                                <ListItemText>Recalculate All</ListItemText>
                            </MenuItem>
                        </If>
                    </Menu>
                </Stack>
            }
            parentCrumbs={parentCrumbs}
            sx={{ height: 1 }}
            downloadButton={
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                        data-cy="scenario-variants-btn"
                        size="small"
                        variant="text"
                        color="primary"
                        startIcon={<Iconify icon="mdi:source-branch" />}
                        onClick={handleOpenVariantPanel}
                        sx={{ fontWeight: 600 }}
                    >
                        {formatVariantCount()}
                    </Button>
                    <Button
                        data-cy="scenario-rules-btn"
                        size="small"
                        variant="text"
                        color="primary"
                        startIcon={<Iconify icon="tabler:filter-cog" />}
                        onClick={handleOpenRulesPanel}
                        sx={{ fontWeight: 600 }}
                    >
                        {formatRuleSetCount()}
                    </Button>
                    <Button
                        data-cy="scenario-compare-btn"
                        size="small"
                        variant="outlined"
                        startIcon={<Iconify icon="mdi:compare-horizontal" />}
                        onClick={handleCompareVariants}
                    >
                        Compare Variants
                    </Button>
                </Stack>
            }
        >
            <Stack data-cy="scenario-view" spacing={2} sx={{ pt: 2, flex: 1 }}>
                <If value={promotedRegimeId}>
                    <PromotedRegimeBanner regimeId={promotedRegimeId} onDismiss={clearPromotedRegime} />
                </If>
                <VariantPanel
                    open={variantPanelOpen}
                    onClose={handleCloseVariantPanel}
                    variants={variants}
                    selectedVariantId={effectiveVariantId}
                    onSelect={setSelectedVariantId}
                    onAdd={variantActions.onAdd}
                    onEdit={variantActions.onEdit}
                    onRemove={variantActions.onRemove}
                    onRecalculate={variantActions.onRecalculate}
                    onReorder={variantActions.onReorder}
                    onPromote={variantActions.onPromote}
                />
                <RulesPanel
                    open={rulesPanelOpen}
                    onClose={handleCloseRulesPanel}
                    ruleSets={effectiveRuleSets}
                    appliedRuleSetIds={appliedRuleSetIds}
                    onAddRuleSets={rulesActions.onAddRuleSets}
                    onCreateRuleSet={rulesActions.onCreateRuleSet}
                    onEditRuleSet={rulesActions.onEditRuleSet}
                    onRemoveRuleSet={rulesActions.onRemoveRuleSet}
                    onSaveRuleSetOrder={rulesActions.onSaveRuleSetOrder}
                />
                <Bound
                    onToggleModifier={costSummaryActions.onToggleModifier}
                    onRecalculateModifiers={costSummaryActions.onRecalculateModifiers}
                >
                    <ScenarioCostSummary
                        data={buildVariantData()}
                        canManageOptimiser={canManageOptimiser}
                        onEditScenario={costSummaryActions.onEditScenario}
                        activeVariantName={selectedVariant?.name}
                        variantCount={variants.length}
                        onSwitchVariant={handleOpenVariantPanel}
                    />
                </Bound>
            </Stack>
        </PageBreadcrumbContainer>
    )

    function buildEffectiveRuleSets() {
        if (!variantRuleSetIds.length) return []

        const items = variantRuleSetsResult?.items ?? []
        const byId = new Map(items.map(toRuleSetEntry))
        const ruleSets = []

        for (let index = 0; index < variantRuleSetIds.length; index += 1) {
            const ruleSetId = variantRuleSetIds[index]
            ruleSets.push(buildEffectiveRuleSet(ruleSetId, index, byId))
        }

        return ruleSets
    }

    function buildEffectiveRuleSet(ruleSetId, index, byId) {
        const fetched = byId.get(ruleSetId)
        const cached = cachedRuleSetsById[ruleSetId]
        if (!fetched) return cached || buildFallbackRuleSet(ruleSetId, index)
        if (cached) return { ...fetched, ...cached }
        if (hasRules(fetched)) return fetched
        return fetched
    }

    function buildFallbackRuleSet(ruleSetId, index) {
        return {
            id: ruleSetId,
            name: getVariantRuleSetName(index, ruleSetId),
        }
    }

    function getVariantRuleSetName(index, ruleSetId) {
        return selectedVariant?.ruleSetNames?.[index] || selectedVariant?.ruleSetName || ruleSetId
    }

    function getAppliedRuleSetIds() {
        return [...new Set(variantRuleSetIds)]
    }

    function getVariants() {
        const sourceVariants = data?.variants ?? []
        if (Object.keys(variantOverridesById).length === 0) return sourceVariants
        return sourceVariants.map(applyVariantOverride)
    }

    function applyVariantOverride(variant) {
        const override = variantOverridesById[variant.id]
        if (!override) return variant
        return { ...variant, ...override }
    }

    function clearSettledVariantOverrides() {
        const overrideEntries = Object.entries(variantOverridesById)
        if (overrideEntries.length === 0) return

        const nextOverrides = {}
        let changed = false

        for (const [variantId, override] of overrideEntries) {
            const serverVariant = (data?.variants || []).find(byId(variantId))
            if (isSettledVariantOverride(serverVariant, override)) {
                changed = true
            } else {
                nextOverrides[variantId] = override
            }
        }

        if (changed) setVariantOverridesById(nextOverrides)
    }

    function findSelectedVariant() {
        return variants.find((v) => v.id === effectiveVariantId) || variants[0] || null
    }

    function getVariantRuleSetIds() {
        return getRuleSetIdsForVariant(selectedVariant)
    }

    function buildVariantData() {
        if (!selectedVariant) return data
        return {
            ...data,
            status: selectedVariant.status,
            costResult: selectedVariant.costResult,
            isStale: selectedVariant.isStale,
            rateCardId: selectedVariant.rateCardId,
            rateCardName: selectedVariant.rateCardName,
            rateCardIsArchived: selectedVariant.rateCardIsArchived,
            rateCardModifiers: selectedVariant.rateCardModifiers,
            disabledModifierIds: selectedVariant.disabledModifierIds || [],
            hasPendingModifierChanges: hasPendingModifierChanges(),
            calculatedAt: selectedVariant.calculatedAt,
            lastCalculationError: selectedVariant.lastCalculationError,
            _variantId: selectedVariant.id,
        }
    }

    function getVariantActions() {
        if (!canManageOptimiser) return {}

        return {
            onAdd: handleAddVariantFromPanel,
            onEdit: handleEditVariant,
            onRemove: handleRemoveVariant,
            onRecalculate: handleRecalculateVariant,
            onReorder: handleReorderVariants,
            onPromote: handlePromoteVariant,
        }
    }

    function getRulesActions() {
        if (!canManageOptimiser) return {}

        return {
            onAddRuleSets: handleAddRuleSetsFromPanel,
            onCreateRuleSet: handleCreateRuleSetForScenario,
            onEditRuleSet: handleEditRuleSetFromPanel,
            onRemoveRuleSet: handleRemoveRuleSetFromPanel,
            onSaveRuleSetOrder: handleSaveRuleSetOrderFromPanel,
        }
    }

    function getCostSummaryActions() {
        if (!canManageOptimiser) return {}

        return {
            onEditScenario: handleEditScenario,
            onToggleModifier: handleToggleModifier,
            onRecalculateModifiers: handleRecalculateModifiers,
        }
    }

    function handleOpenMenu(event) {
        setMenuAnchor(event.currentTarget)
    }

    function handleCloseMenu() {
        setMenuAnchor(null)
    }

    function handleEdit() {
        setMenuAnchor(null)
        navigate(`/app/optimiser/scenarios/${id}/edit`)
    }

    function handleEditScenario() {
        navigate(`/app/optimiser/scenarios/${id}/edit`)
    }

    function handleToggleModifier(modifierId, enabled) {
        const disabledModifierIds = getNextDisabledModifierIds(modifierId, enabled)
        const variantId = selectedVariant?.id
        stageModifierState(variantId, disabledModifierIds)
    }

    async function handleRecalculateModifiers() {
        if (!hasPendingModifierChanges()) return
        const variantId = selectedVariant?.id
        const disabledModifierIds = selectedVariant?.disabledModifierIds || []
        const previousOverride = variantOverridesById[variantId]
        markVariantCalculating(variantId)

        try {
            await updateVariantApi({
                scenarioId: data.id,
                variantId,
                disabledModifierIds,
            })
            markModifierUpdateSubmitted(variantId)
        } catch {
            restoreVariantOverride(variantId, previousOverride)
            errorSnackbar("Unable to update modifiers. Your changes have been kept; please try again.")
        }
    }

    function stageModifierState(variantId, disabledModifierIds) {
        if (!variantId) return
        setVariantOverridesById((previous) => ({
            ...previous,
            [variantId]: {
                ...previous[variantId],
                disabledModifierIds,
            },
        }))
    }

    function markVariantCalculating(variantId) {
        if (!variantId) return
        setVariantOverridesById((previous) => ({
            ...previous,
            [variantId]: {
                ...previous[variantId],
                status: "calculating",
                modifierUpdatePending: true,
            },
        }))
    }

    function markModifierUpdateSubmitted(variantId) {
        if (!variantId) return
        setVariantOverridesById((previous) => ({
            ...previous,
            [variantId]: {
                ...previous[variantId],
                modifierUpdatePending: false,
            },
        }))
    }

    function hasPendingModifierChanges() {
        const serverVariant = (data?.variants || []).find(byId(selectedVariant?.id))
        if (!serverVariant || !selectedVariant) return false
        return !haveSameIds(serverVariant.disabledModifierIds, selectedVariant.disabledModifierIds)
    }

    function getNextDisabledModifierIds(modifierId, enabled) {
        const disabledModifierIds = selectedVariant?.disabledModifierIds || []
        if (enabled) return disabledModifierIds.filter(notMatchingModifierId)
        if (disabledModifierIds.includes(modifierId)) return disabledModifierIds
        return [...disabledModifierIds, modifierId]

        function notMatchingModifierId(id) {
            return id !== modifierId
        }
    }

    function handleCompareVariants() {
        setMenuAnchor(null)
        navigate(`/app/optimiser/scenarios/${id}/compare`)
    }

    async function handleExport() {
        setMenuAnchor(null)
        const exportData = buildVariantData()
        // Database-backed results are exported server-side (the browser never holds
        // the full detail); legacy inline results keep the in-browser workbook.
        if (exportData.costResult?.detailsRef?.setKey && exportData._variantId) {
            await runScenarioCostExport({
                scenarioId: data.id,
                variantIds: [exportData._variantId],
                mode: "single",
            })
            return
        }
        await exportScenarioToExcel({ data: exportData, userName: currentUser.displayName })
    }

    async function handleRecalculateAll() {
        setMenuAnchor(null)
        await recalculateScenario({ id: data.id })
        successSnackbar("Recalculation started")
    }

    async function handleAddVariant() {
        const result = await openAddDialog()
        if (!result) return
        await busyWhile(() => addVariantApi({ scenarioId: data.id, ...result }), "Adding variant...")
        successSnackbar("Variant added")
    }

    async function handleEditVariant(variant) {
        const ids = variant.ruleSetIds || (variant.ruleSetId ? [variant.ruleSetId] : [])
        const names = variant.ruleSetNames || (variant.ruleSetName ? [variant.ruleSetName] : [])
        const ruleSetsForDialog = ids.map(toRuleSetForDialog(names))
        const result = await openEditDialog({
            initialData: {
                name: variant.name,
                rateCard: { id: variant.rateCardId, name: variant.rateCardName },
                ruleSets: ruleSetsForDialog,
            },
        })
        if (!result) return
        await busyWhile(
            () => updateVariantApi({ scenarioId: data.id, variantId: variant.id, ...result }),
            "Updating variant..."
        )
        successSnackbar("Variant updated")
    }

    async function handleRemoveVariant(variantId) {
        const variant = variants.find(byId(variantId))
        const ok = await confirm(`Remove variant "${variant?.name}"?`, "Remove Variant")
        if (!ok) return
        await busyWhile(() => removeVariantApi({ scenarioId: data.id, variantId }), "Removing variant...")
        if (selectedVariantId === variantId) setSelectedVariantId(null)
        successSnackbar("Variant removed")
    }

    async function handleRecalculateVariant(variantId) {
        await recalculateVariantApi({ scenarioId: data.id, variantId })
        successSnackbar("Recalculation started")
    }

    async function handlePromoteVariant(variant) {
        setVariantPanelOpen(false)
        const result = await openPromoteDialog({ scenario: data, variant })
        if (!result) return
        // Kick off the promotion; the heavy tailoring runs in the background and we get notified
        // (via usePromotionNotifications) when the draft regime is ready.
        const promoted = await promoteScenarioToDraftRegime({ scenarioId: data.id, ...result })
        if (!promoted?.regimeId) return
        notifyPromotionStarted(promoted.regimeName)
    }

    async function handleReorderVariants(reordered) {
        const variantIds = reordered.map(toId)
        await reorderVariantsApi({ scenarioId: data.id, variantIds })
    }

    function handleOpenVariantPanel() {
        setVariantPanelOpen(true)
    }

    function handleCloseVariantPanel() {
        setVariantPanelOpen(false)
    }

    async function handleAddVariantFromPanel() {
        handleCloseVariantPanel()
        await handleAddVariant()
    }

    function handleOpenRulesPanel() {
        setRulesPanelOpen(true)
    }

    function handleCloseRulesPanel() {
        setRulesPanelOpen(false)
    }

    async function handleAddRuleSetsFromPanel(selectedRuleSets) {
        await addRuleSetsToSelectedVariant(
            selectedRuleSets,
            "Adding rule sets...",
            "Rule sets added. Recalculation started"
        )
    }

    async function addRuleSetsToSelectedVariant(selectedRuleSets, busyMessage, successMessage) {
        const nextRuleSetIds = mergeRuleSetIds(variantRuleSetIds, selectedRuleSets)
        if (areRuleSetIdsEqual(nextRuleSetIds, variantRuleSetIds)) return false

        const previousOverride = variantOverridesById[selectedVariant?.id]
        cacheRuleSets(selectedRuleSets)
        applyOptimisticVariantRuleSets(nextRuleSetIds, selectedRuleSets)

        try {
            await busyWhile(() => updateSelectedVariantRuleSets(nextRuleSetIds), busyMessage)
            successSnackbar(successMessage)
            return true
        } catch (e) {
            restoreVariantOverride(selectedVariant?.id, previousOverride)
            throw e
        }
    }

    async function handleSaveRuleSetOrderFromPanel(ruleSetIds) {
        if (areRuleSetIdsEqual(ruleSetIds, variantRuleSetIds)) return false

        const previousOverride = variantOverridesById[selectedVariant?.id]
        const orderedRuleSets = getRuleSetsForIds(ruleSetIds)
        applyOptimisticVariantRuleSets(ruleSetIds, orderedRuleSets)

        try {
            await busyWhile(() => updateSelectedVariantRuleSets(ruleSetIds), "Saving rule set order...")
            successSnackbar("Rule set order saved. Recalculation started")
            return true
        } catch (e) {
            restoreVariantOverride(selectedVariant?.id, previousOverride)
            throw e
        }
    }

    async function handleCreateRuleSetForScenario() {
        const regime = data?.regimeId ? { id: data.regimeId, label: data.regimeName } : null
        const newSet = await createRuleSetForScenario(regime)
        if (!newSet?.id) return null
        await addRuleSetsToSelectedVariant(
            [{ ...newSet, id: newSet.id }],
            "Adding rule set...",
            "Rule set created and added. Recalculation started"
        )
        return newSet
    }

    async function handleEditRuleSetFromPanel(ruleSet) {
        const regime = data?.regimeId ? { id: data.regimeId, label: data.regimeName } : null
        const result = await openEditRuleSetDialog({ ruleSet, regime })
        if (!result) return
        const rulesForSave = stripRuleSetRules(result.rules)
        const rulesForCache = restoreRuleSetReferenceNames(stripRuleSetRulesForCache(result.rules), ruleSet.rules)
        const needsRecalculationPrompt = hasCalculationRelevantRuleSetChange(ruleSet, result, rulesForSave)
        await busyWhile(
            () =>
                updateRuleSet({
                    id: ruleSet.id,
                    data: {
                        name: result.name,
                        description: result.description,
                        justification: result.justification,
                        defaultAction: result.defaultAction,
                        parentRuleSetId: null,
                        rules: rulesForSave,
                    },
                }),
            "Saving rule set..."
        )
        cacheRuleSets([
            {
                ...ruleSet,
                name: result.name,
                description: result.description,
                justification: result.justification,
                defaultAction: result.defaultAction,
                rules: rulesForCache,
            },
        ])
        await handleRuleSetUpdated(ruleSet.id, needsRecalculationPrompt)
    }

    async function handleRemoveRuleSetFromPanel(ruleSet) {
        const ok = await confirm(`Remove "${ruleSet?.name || "this rule set"}" from this scenario?`, "Remove Rule Set")
        if (!ok) return
        const nextRuleSetIds = variantRuleSetIds.filter(notMatchingId(ruleSet?.id))
        if (areRuleSetIdsEqual(nextRuleSetIds, variantRuleSetIds)) return

        const previousOverride = variantOverridesById[selectedVariant?.id]
        applyOptimisticVariantRuleSets(nextRuleSetIds)

        try {
            await busyWhile(() => updateSelectedVariantRuleSets(nextRuleSetIds), "Removing rule set...")
            successSnackbar("Rule set removed. Recalculation started")
        } catch (e) {
            restoreVariantOverride(selectedVariant?.id, previousOverride)
            throw e
        }
    }

    async function updateSelectedVariantRuleSets(nextRuleSetIds) {
        const scenarioId = data?.id
        const variantId = selectedVariant?.id
        if (!scenarioId || !variantId) return

        await updateVariantApi({
            scenarioId,
            variantId,
            ruleSetIds: nextRuleSetIds,
        })
    }

    function applyOptimisticVariantRuleSets(ruleSetIds, selectedRuleSets) {
        const variantId = selectedVariant?.id
        if (!variantId) return
        const ruleSetNames = buildRuleSetNames(ruleSetIds, selectedRuleSets)
        setVariantOverridesById((previous) => ({
            ...previous,
            [variantId]: {
                ...previous[variantId],
                ruleSetId: ruleSetIds[0] || null,
                ruleSetIds,
                ruleSetName: ruleSetNames[0] || null,
                ruleSetNames,
                status: "calculating",
            },
        }))
    }

    function restoreVariantOverride(variantId, previousOverride) {
        if (!variantId) return
        setVariantOverridesById((previous) => {
            const next = { ...previous }
            if (previousOverride) {
                next[variantId] = previousOverride
            } else {
                delete next[variantId]
            }
            return next
        })
    }

    function buildRuleSetNames(ruleSetIds, selectedRuleSets) {
        return ruleSetIds.map((ruleSetId, index) => getKnownRuleSetName(ruleSetId, index, selectedRuleSets))
    }

    function getKnownRuleSetName(ruleSetId, index, selectedRuleSets) {
        const selectedRuleSet = selectedRuleSets?.find(byId(ruleSetId))
        if (selectedRuleSet?.name) return selectedRuleSet.name
        if (cachedRuleSetsById[ruleSetId]?.name) return cachedRuleSetsById[ruleSetId].name
        const effectiveRuleSet = effectiveRuleSets.find(byId(ruleSetId))
        if (effectiveRuleSet?.name) return effectiveRuleSet.name
        return getVariantRuleSetName(index, ruleSetId)
    }

    function getRuleSetsForIds(ruleSetIds) {
        const ruleSets = []
        for (const ruleSetId of ruleSetIds || []) {
            const ruleSet = getRuleSetForId(ruleSetId)
            if (ruleSet) ruleSets.push(ruleSet)
        }
        return ruleSets
    }

    function getRuleSetForId(ruleSetId) {
        if (cachedRuleSetsById[ruleSetId]) return cachedRuleSetsById[ruleSetId]
        return effectiveRuleSets.find((ruleSet) => ruleSet.id === ruleSetId)
    }

    async function handleRuleSetUpdated(ruleSetId, needsRecalculationPrompt) {
        if (!needsRecalculationPrompt) {
            successSnackbar("Rule set updated")
            return
        }

        const recalculated = await promptForRuleSetRecalculation(ruleSetId)
        if (recalculated) {
            successSnackbar("Rule set updated. Recalculation started")
        } else {
            successSnackbar("Rule set updated")
        }
    }

    async function promptForRuleSetRecalculation(ruleSetId) {
        const affectedVariantIds = getAffectedVariantIdsForRuleSet(ruleSetId)
        if (!affectedVariantIds.length) return false
        const ok = await confirm(
            buildRecalculationPrompt(affectedVariantIds.length),
            "Recalculate Scenario?",
            undefined,
            "Recalculate now"
        )
        if (!ok) return false
        await busyWhile(() => recalculateAffectedVariants(affectedVariantIds), "Starting recalculation...")
        return true
    }

    function getAffectedVariantIdsForRuleSet(ruleSetId) {
        return variants
            .filter((variant) => getRuleSetIdsForVariant(variant).includes(ruleSetId))
            .map((variant) => variant.id)
    }

    async function recalculateAffectedVariants(variantIds) {
        if (variantIds.length === variants.length) {
            await recalculateScenario({ id: data.id })
            return
        }

        for (const variantId of variantIds) {
            await recalculateVariantApi({ scenarioId: data.id, variantId })
        }
    }

    function cacheRuleSets(ruleSetsToCache) {
        setCachedRuleSetsById((previous) => {
            const next = { ...previous }
            for (const ruleSet of ruleSetsToCache || []) {
                if (ruleSet?.id) next[ruleSet.id] = ruleSet
            }
            return next
        })
    }

    function formatVariantCount() {
        if (variants.length === 1) return "1 Variant"
        return `${variants.length} Variants`
    }

    function formatRuleSetCount() {
        if (ruleSetCount === 1) return "1 Rule Set"
        return `${ruleSetCount} Rule Sets`
    }
}
